import { compact } from 'lodash';
import throttleActions from 'redux-throttle';
import thunk from 'redux-thunk';
import { loadingBarMiddleware } from 'react-redux-loading-bar';
import { routerMiddleware } from 'react-router-redux';
// eslint-disable-next-line import/no-unresolved
import Sentry from 'sentry';

import { PUB_SUB_CHANNEL_APP } from '@wix/communities-blog-client-common/dist/src/constants/pub-sub-channels';
import { EXPERIMENT_COLLECT_METRICS } from '@wix/communities-blog-experiments';
import requestCredentials from '../services/request-credentials';
import createShowMessage from '../services/create-show-message';
import uploadFile from '../services/upload-file';

import { appUouBiMiddleware, appUsersBiMiddleware } from '../bi-events/init-middleware';
import {
  createDuplexerConnection,
  createDuplexerMiddleware,
  socketEventHandlers,
} from '../middleware/duplexer-middleware';
import modalMiddleware, { appModalResolvers, postEditorModalResolvers } from '../middleware/modal-middleware';
import publishMiddleware from '../middleware/publish-middleware';
import subscribeMiddleware from '../middleware/subscribe-middleware';
import redirectMiddleware from '../middleware/redirect-middleware';
import uploadMiddleware from '../middleware/upload-middleware';
import postClearMiddleware from '../middleware/post-clear-middleware';
import postPublishMiddleware from '../middleware/post-publish-middleware';
import actionLoggerMiddleware, { shouldLogReduxActions } from '../middleware/action-logger-middleware';
import setPageMetadataMiddleware from '../middleware/set-page-metadata-middleware';
import errorHandlerMiddleware from '../middleware/error-handler-middleware';
import createOneAppMiddleware from '../middleware/one-app-middleware/create-blog-one-app-middleware';
import appSettingsModalMiddleware from '../../app-settings/middleware/app-settings-modal-middleware';
import appSettingsClosedMiddleware from '../middleware/app-settings-closed-middleware';

import debounceActions from '@sarunas/redux-debounce';
import { eventMap, oneAppEventMap } from '../app-uou-events';
import { openMobilePostEditorModal } from '../services/post-editor';
import { setConfig, getConfig } from '../services/config';
import { getInstance, isEditor } from '../services/wix-sdk-utils';
import createRequest from '@wix/communities-blog-client-common/dist/src/services/create-request';
import createCancellableRequest from '../services/create-cancellable-request';
import { replace } from '../services/router-utils';
import clientPerformanceTracker from '../services/performance-tracker';

import Wix from '../services/wix-sdk-polyfill';

const initDuplexerMiddleware = ({ config, instance, instanceId, apiBaseUrl }) => {
  if (process.env.NODE_ENV !== 'production') {
    return null;
  }

  const captureMessage = (message, extras) =>
    Sentry.withScope(scope => {
      Object.keys(extras).forEach(name => scope.setExtra(name, extras[name]));
      Sentry.captureMessage(`duplexerMiddleware: ${message}`);
    });

  return createDuplexerMiddleware({
    duplexerConnection: createDuplexerConnection({
      duplexerApiKey: config.duplexer.key,
      duplexerServerUrl: config.duplexer.url,
      blogApiBaseUrl: apiBaseUrl,
      instance,
    }),
    eventHandlers: socketEventHandlers,
    instanceId,
    captureMessage,
  });
};

const getMiddleware = ({
  config,
  experiments,
  i18n,
  browserHistory,
  usersEventMap,
  isOneApp,
  isIphone,
  isInPostPreview = false,
}) => {
  setConfig(config);

  const isCollectMetricsEnabled = experiments[EXPERIMENT_COLLECT_METRICS] === 'true';
  const baseUrl = window.__BASE_PATH__ || '';
  const apiBaseUrl = window.__API_BASE_URL__ || '';
  const instance = getInstance();
  const instanceId = getConfig().instanceId;
  const locale = window.__LOCALE__;
  let performanceTracker;

  if (isCollectMetricsEnabled) {
    window.__CLIENT_METRICS__ = {};
    performanceTracker = clientPerformanceTracker;
  }

  const request = createRequest({ apiBaseUrl, instance, instanceId, locale, performanceTracker });
  const cancellableRequest = createCancellableRequest({ apiBaseUrl, instance, locale });

  const middlewares = compact([
    errorHandlerMiddleware,
    publishMiddleware(Wix.PubSub.publish),
    subscribeMiddleware(Wix.PubSub.subscribe, Wix.PubSub.unsubscribe, PUB_SUB_CHANNEL_APP),
    createOneAppMiddleware({ origin: 'app', i18n }),
    loadingBarMiddleware(),
    routerMiddleware(browserHistory),
    postPublishMiddleware({
      onPublish: (store, post) => store.dispatch(replace(`/${post.slug}`)),
      onPostEditorSet: store => openMobilePostEditorModal({ state: store.getState(), dispatch: store.dispatch }),
    }),
    throttleActions(),
    debounceActions(),
    redirectMiddleware,
    uploadMiddleware(uploadFile, requestCredentials, request),
    thunk.withExtraArgument({
      request,
      cancellableRequest,
      baseUrl: baseUrl || `${window.location.protocol}//${window.location.host}`,
    }),
    !isInPostPreview && initDuplexerMiddleware({ config, instance, instanceId, apiBaseUrl }),
    appUouBiMiddleware({
      eventMap: isOneApp ? oneAppEventMap : eventMap,
      useBeacon: !(isOneApp && isIphone),
    }),
    modalMiddleware({
      createShowMessage,
      resolvers: { ...appModalResolvers, ...postEditorModalResolvers },
    }),
    setPageMetadataMiddleware,
  ]);

  if (isEditor()) {
    middlewares.push(appSettingsModalMiddleware(), appSettingsClosedMiddleware);
  }

  config.bundleName === 'app' && middlewares.push(postClearMiddleware());

  shouldLogReduxActions() && middlewares.push(actionLoggerMiddleware);

  usersEventMap && middlewares.push(appUsersBiMiddleware({ eventMap: usersEventMap }));

  return middlewares;
};

export default getMiddleware;
